import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DollarSign, Clock, CheckCircle } from 'lucide-react';
import { DecimalKeypad } from '@/components/ui/keypads';
import { OpenCashRegisterData } from '../../types/cash-register.types';
import { cashRegisterService } from '../../services/cashRegisterService';
import { useLocationShifts } from '@/hooks/useLocationShifts';
import { formatFullDatePeru } from '@/utils/dateUtils';

interface CashRegisterOpeningProps {
  onOpen: (data: OpenCashRegisterData) => Promise<void>;
  loading?: boolean;
  locationId: string;
  date: string;
  currentShift?: string;
  shiftSchedule?: any;
}

export const CashRegisterOpening: React.FC<CashRegisterOpeningProps> = ({
  onOpen,
  loading = false,
  locationId,
  date,
  currentShift,
  shiftSchedule
}) => {
  const [openingAmount, setOpeningAmount] = useState<string>('');
  const [suggestedAmount, setSuggestedAmount] = useState<number>(0);
  const [loadingSuggestion, setLoadingSuggestion] = useState<boolean>(true);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [keypadOpen, setKeypadOpen] = useState<boolean>(false);

  // Usar el hook para obtener información de turnos basada en location_schedules
  console.log('🔍 DEBUG CashRegisterOpening props:', { locationId, currentShift, shiftSchedule });
  const { getShiftDisplay } = useLocationShifts(locationId);
  const shiftDisplayInfo = getShiftDisplay();
  console.log('📊 DEBUG CashRegisterOpening shiftDisplayInfo:', shiftDisplayInfo);

  // Determinar el texto del turno a mostrar
  const getDisplayShift = () => {
    // Solo usar currentShift si no es 'CERRADO' (ignorar estados erróneos)
    if (currentShift && currentShift !== 'CERRADO') {
      return currentShift;
    }

    // Usar la información del hook de turnos unificado
    if (shiftDisplayInfo.type === 'split') {
      // Si hay turnos separados, mostrar el turno actual activo
      const currentTime = new Date().getHours() * 100 + new Date().getMinutes();

      if (shiftDisplayInfo.shifts) {
        for (const shift of shiftDisplayInfo.shifts) {
          const [startHour, startMin] = shift.start_time.split(':').map(Number);
          const [endHour, endMin] = shift.end_time.split(':').map(Number);
          const shiftStart = startHour * 100 + startMin;
          const shiftEnd = endHour * 100 + endMin;

          if (currentTime >= shiftStart && currentTime <= shiftEnd) {
            return shift.name;
          }
        }
      }
      return 'Mañana/Tarde'; // Fallback para turno split
    }

    return shiftDisplayInfo.text; // 'Mañana', 'Cerrado', etc.
  };

  const displayShift = getDisplayShift();

  // Load suggested opening amount when component mounts
  useEffect(() => {
    const loadSuggestedAmount = async () => {
      try {
        setLoadingSuggestion(true);
        const suggested = await cashRegisterService.getSuggestedOpeningAmount(locationId);
        setSuggestedAmount(suggested);
        // Only set suggested amount if there's actual history, otherwise leave empty for user to enter
        setOpeningAmount(suggested > 0 ? suggested.toFixed(2) : '');
      } catch (error) {
        console.error('Error loading suggested amount:', error);
        setOpeningAmount(''); // Start empty if no history available
      } finally {
        setLoadingSuggestion(false);
      }
    };

    loadSuggestedAmount();
  }, [locationId]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    const amount = parseFloat(openingAmount);
    if (isNaN(amount) || amount < 0) {
      newErrors.openingAmount = 'Ingresa un monto válido';
    }
    if (amount > 10000) {
      newErrors.openingAmount = 'El monto parece muy alto. Verifica el valor.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onOpen({
        locationId,
        date,
        openingAmount: parseFloat(openingAmount)
      });
    } catch (error) {
      console.error('Error opening cash register:', error);
    }
  };

  // Usar formatFullDatePeru de dateUtils que maneja correctamente las zonas horarias

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className="shadow-lg">
        <CardHeader className="text-center pb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Apertura de Caja
          </CardTitle>
          <div className="flex items-center justify-center gap-2 text-gray-600 mt-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">{formatFullDatePeru(date)}</span>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Monto inicial */}
            <div className="space-y-2">
              <Label htmlFor="opening-amount" className="text-sm font-medium text-gray-700">
                Monto inicial en caja *
              </Label>
              {loadingSuggestion && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  <span>Calculando monto sugerido...</span>
                </div>
              )}
              {!loadingSuggestion && suggestedAmount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-2">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Monto sugerido:</span> S/ {suggestedAmount.toFixed(2)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Basado en el último cierre de caja
                  </p>
                </div>
              )}
              <Input
                id="opening-amount"
                type="text"
                value={openingAmount ? `S/ ${parseFloat(openingAmount).toFixed(2)}` : 'S/ 0.00'}
                onClick={() => !loading && !loadingSuggestion && setKeypadOpen(true)}
                readOnly
                className={`text-lg font-medium cursor-pointer bg-white hover:bg-gray-50 transition-colors ${errors.openingAmount ? 'border-red-300 focus:border-red-500' : ''}`}
                placeholder="S/ 0.00"
                disabled={loading || loadingSuggestion}
              />
              {errors.openingAmount && (
                <p className="text-sm text-red-600">{errors.openingAmount}</p>
              )}
              <p className="text-xs text-gray-500">
                {suggestedAmount > 0 
                  ? 'Monto calculado automáticamente desde el último cierre. Puedes modificarlo si es necesario.'
                  : 'Ingresa el efectivo inicial con el que abres la caja (billetes y monedas)'
                }
              </p>
            </div>


            {/* Resumen */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-gray-900">Resumen de apertura:</h4>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fecha:</span>
                <span className="font-medium">{formatFullDatePeru(date)}</span>
              </div>
              <div className="flex justify-between items-center bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-blue-700 font-medium">Turno:</span>
                <span className="font-bold text-blue-800 text-lg">{displayShift}</span>
                {!currentShift && (
                  <span className="text-xs text-blue-600 ml-2">(estimado)</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monto inicial:</span>
                <span className="font-bold text-green-600 text-lg">
                  S/ {parseFloat(openingAmount || '0').toFixed(2)}
                </span>
              </div>
            </div>

            {/* Botón de apertura */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-lg font-medium"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Abriendo caja...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Abrir Caja del Día
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
      <DecimalKeypad
        isOpen={keypadOpen}
        onClose={() => setKeypadOpen(false)}
        onSubmit={(value) => setOpeningAmount(value)}
        title="Monto inicial en caja"
        initialValue="0.00"
      />
    </div>
  );
};